import React, { useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { CaretLeft, Wrench } from '@phosphor-icons/react'
import { Button, IconButton } from '@shopflo/ui'
import DiscountCreationFlow from 'src/components/discounts/discount-creation-flow/DiscountCreationFlow'
import SaveDiscountDialog from 'src/components/discounts/v2/dialogs/SaveDiscountDialog'
import { DiscountFormContent } from 'src/components/discounts/v2/DiscountCreationFlowV2'
import { useDiscountForm } from 'src/components/discounts/v2/hooks/useDiscountForm'
import { useLocale } from 'src/lib/hooks/useLocal'
import { CopiedDiscountInfo } from 'src/lib/types/common/discounts'
import { DiscountFlowConfigType } from 'src/lib/types/discounts'
import { ExperimentCreatedDiscountType } from 'src/lib/types/experiments'

interface DiscountCreationContentProps {
  setIsOpen: (isOpen: boolean) => void
  copiedDiscountInfo?: CopiedDiscountInfo
  setCreatedDiscount: (discountData: ExperimentCreatedDiscountType) => void
  flowConfig: DiscountFlowConfigType
  context: 'experiment' | 'none'
}

const DiscountCreationContent = ({
  setIsOpen,
  copiedDiscountInfo,
  setCreatedDiscount,
  flowConfig,
  context
}: DiscountCreationContentProps) => {
  const { t } = useLocale()
  const [isLegacyLayout, setIsLegacyLayout] = useState(context === 'experiment' ? true : false)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)

  const {
    actions: { validateForm }
  } = useDiscountForm()

  const handleSaveClick = () => {
    if (validateForm()) {
      setIsSaveDialogOpen(true)
    }
  }

  if (isLegacyLayout) {
    return (
      <div className='flex flex-col w-full gap-6'>
        <header className='flex flex-row items-center gap-4'>
          <button
            className='px-3 py-2 bg-white border rounded-lg max-w-fit border-flo-gray-light'
            onClick={() => setIsOpen(false)}
          >
            <ArrowLeft size='16' className='font-medium text-coal-dark ' />
          </button>
          <h1 className='text-lg font-semibold text-carbon'>
            {copiedDiscountInfo
              ? "Creating a copy of '" + copiedDiscountInfo.title + "'"
              : t('create_variant_discount')}
          </h1>
        </header>
        <>
          <section className='flex w-full'>
            <div className='w-full'>
              <DiscountCreationFlow
                flowConfig={flowConfig}
                discountId={undefined}
                setCreatedDiscount={setCreatedDiscount}
                handleClose={() => setIsOpen(false)}
                copiedDiscountInfo={copiedDiscountInfo}
                context={context}
              />
            </div>
          </section>
        </>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full'>
      <header className='flex items-center justify-between px-12 pt-5 pb-2 z-20 '>
        <div className='flex items-center gap-2'>
          <IconButton
            variant='outline'
            IconComponent={CaretLeft}
            onClick={() => setIsOpen(false)}
            size='sm'
            className='text-zinc-500'
            iconProps={{ weight: 'bold', className: 'w-3 h-3' }}
          />

          <h1 className='text-text-lg-semibold'>
            {copiedDiscountInfo
              ? "Creating a copy of '" + copiedDiscountInfo.title + "'"
              : t('create_variant_discount')}
          </h1>
        </div>

        <div className='flex items-center gap-1.5'>
          {flowConfig.type !== 'create-exit-coupon' && (
            <>
              <Button
                variant='ghost'
                size='sm'
                leftIcon={Wrench}
                className='text-zinc-500'
                onClick={() => {
                  setIsLegacyLayout(true)
                }}
              >
                {t('discounts_v2.legacy_builder')}
              </Button>
              <div className='w-2 justify-center flex'>
                <div className='h-6 w-[1px] bg-zinc-200'></div>
              </div>
            </>
          )}
          <div className='flex items-center gap-1'>
            <Button variant='outline' size='sm' onClick={() => setIsOpen(false)}>
              {t('cancel')}
            </Button>
            <Button variant='primary' size='sm' onClick={handleSaveClick}>
              {t('save')}
            </Button>
          </div>
        </div>
      </header>

      <div className='flex-1 overflow-y-auto'>
        <DiscountFormContent
          discountId={copiedDiscountInfo?.id}
          copiedDiscountInfo={copiedDiscountInfo}
          ignoreLayout={true}
        />
      </div>

      <SaveDiscountDialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen} />
    </div>
  )
}

export default DiscountCreationContent
